import {
  CIRCLE_ROW,
  printedPages,
  CUTOUT_STRIP,
  GLUE_BOX,
  MATCH_ROW,
  PAGE,
  RULE_GAP,
  RULE_HEIGHT,
  SINGLE_BOX,
  type Worksheet,
  type WorksheetPart,
} from '@td/shared';

/**
 * A worksheet, as a sheet of paper.
 *
 * Everything is drawn with borders and spacing rather than images, so it
 * prints on any printer in any classroom without a single download. The
 * measurements are the ones that matter for six-year-olds: handwriting rules
 * at 30px with a dashed midline, boxes big enough for a fist holding a fat
 * pencil, and a name line at the top because there is always one child who
 * forgets.
 */
export function WorksheetSheet({ sheet }: { sheet: Worksheet }) {
  /*
   * One article per sheet of paper. They are siblings, so the print rule that
   * breaks after every `.worksheet` except the last one already does the right
   * thing for a two-page worksheet without knowing anything about pages.
   */
  const pages = printedPages(sheet.parts);

  return (
    <>
      {pages.map((parts, page) => (
        <article
          key={page}
          /*
           * On screen it is a preview at the width of the paper; on paper it
           * is the paper, and the page box already supplies the margin. Its
           * height is the printable area exactly, so a worksheet either fits
           * or the check that guards them fails in CI rather than at the
           * photocopier.
           */
          style={{ maxWidth: PAGE.width - PAGE.margin * 2 }}
          className="worksheet mx-auto mb-3 w-full bg-white p-8 text-espresso last:mb-0 print:p-0"
        >
          <header className="mb-4 border-b-2 border-espresso pb-2">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-display text-2xl leading-tight">
                {sheet.title}
                {/*
                  Both sheets carry the title and the name line. Pages get
                  separated on the way to a desk, and a page of a six-year-old's
                  work with no name on it belongs to nobody.
                */}
                {pages.length > 1 && (
                  <span className="ml-2 align-middle text-base text-stone">
                    page {page + 1} of {pages.length}
                  </span>
                )}
              </h2>
              <p className="whitespace-nowrap text-sm">
                Name <span className="ml-1 inline-block w-44 border-b border-espresso" />
              </p>
            </div>

            {/* Read aloud once, before they start — so only on the first sheet. */}
            {page === 0 && sheet.instructions && (
              <p className="mt-2 text-[15px] leading-snug">{sheet.instructions}</p>
            )}
          </header>

          {/*
            Ordered, not just listed. A cut-out strip prints last on its own
            page whatever order it was written in — everything below the dashed
            line leaves the page with the cut-outs, so a question underneath
            one goes in the bin.
          */}
          <div className="space-y-4">
            {parts.map((part, i) => (
              <Part key={i} part={part} />
            ))}
          </div>

          {page === pages.length - 1 && sheet.standards.length > 0 && (
            <footer className="mt-6 border-t border-sand pt-2 text-[10px] text-stone">
              Indiana Academic Standards: {sheet.standards.join(', ')}
            </footer>
          )}
        </article>
      ))}
    </>
  );
}

function Part({ part }: { part: WorksheetPart }) {
  switch (part.kind) {
    case 'text':
      return <p className="text-[15px] leading-snug">{part.text}</p>;

    case 'handwriting':
      // One string per line, so a page can hand a child the whole alphabet
      // rather than the first ten letters of it.
      return <Handwriting lines={part.lines} trace={part.trace} />;

    case 'lines':
      /*
       * Ruled, not plain. A first grader writes between three lines — solid
       * top, dashed middle, solid bottom — and a single underline gives them
       * nothing to size a letter against. There is no such thing as a plain
       * writing line on a page for this age, so `lines` and `handwriting`
       * draw the same thing and differ only in whether anything is printed
       * to trace.
       */
      return <Handwriting lines={part.lines} />;

    case 'boxes':
      return (
        <div>
          {part.label && <p className="mb-1 text-sm">{part.label}</p>}
          <div
            className="grid gap-1"
            style={{ gridTemplateColumns: `repeat(${part.columns}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: part.count }, (_, i) => (
              <div
                key={i}
                className="border-2 border-espresso"
                // One box is a working-out space; a row of them is a graph.
                style={{ height: part.count === 1 ? SINGLE_BOX : 30 }}
              />
            ))}
          </div>
        </div>
      );

    case 'problems':
      return (
        <ol style={{ display: 'grid', rowGap: 10 }}>
          {part.problems.map((problem, i) => (
            <li key={i} className="flex gap-2">
              <span className="w-5 shrink-0 text-right text-[15px] leading-8">{i + 1}.</span>

              {part.answerLines > 0 ? (
                <div className="min-w-0 flex-1">
                  <p className="text-[17px] leading-snug">{problem}</p>
                  <div className="mt-2">
                    <Handwriting lines={part.answerLines} />
                  </div>
                </div>
              ) : (
                /*
                 * A sum and its answer box on one line.
                 *
                 * The box used to follow a <p>, and a paragraph is a block —
                 * so every sum took two lines instead of one. Six of them
                 * added three hundred pixels and pushed the sheet onto a
                 * second page that was nine-tenths empty.
                 */
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="text-[17px] leading-8">{problem}</span>
                  <span className="h-8 w-16 shrink-0 border-2 border-espresso" />
                </div>
              )}
            </li>
          ))}
        </ol>
      );

    case 'wordbank':
      return (
        <div className="rounded border-2 border-dashed border-espresso p-2">
          <p className="mb-1 text-center text-xs uppercase tracking-wide">Word bank</p>
          <p className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[15px]">
            {part.words.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </p>
        </div>
      );

    case 'drawbox':
      return (
        <div>
          <div className="border-2 border-espresso" style={{ height: part.height }} />
          <p className="mt-1 text-center text-xs">{part.caption}</p>
        </div>
      );

    case 'numberline':
      return <NumberLine from={part.from} to={part.to} step={part.step} />;

    case 'tenframe':
      return (
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: part.frames }, (_, f) => (
            <div key={f} className="grid grid-cols-5 grid-rows-2">
              {Array.from({ length: 10 }, (_, c) => (
                <span key={c} className="h-8 w-8 border border-espresso" />
              ))}
            </div>
          ))}
        </div>
      );

    case 'checklist':
      return (
        <ul className="space-y-1">
          {part.items.map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm">
              <span className="h-4 w-4 shrink-0 border-2 border-espresso" />
              {item}
            </li>
          ))}
        </ul>
      );

    case 'table':
      return (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {part.headers.map((h) => (
                <th
                  key={h}
                  className="border-2 border-espresso bg-linen px-2 py-1 text-left text-sm font-normal"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: part.rows }, (_, r) => (
              <tr key={r}>
                {part.headers.map((h) => (
                  <td key={h} className="h-10 border-2 border-espresso" />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'matching':
      return <Matching left={part.left} right={part.right} />;

    case 'circleit':
      return (
        <div style={{ display: 'grid', rowGap: 8 }}>
          {part.questions.map((q, i) => (
            <div key={i}>
              <p className="text-[15px] leading-snug">{q.prompt}</p>
              <div className="flex flex-wrap items-center gap-3" style={{ height: CIRCLE_ROW }}>
                {q.options.map((option) => (
                  /*
                   * Big enough to circle with a fat pencil, and spaced far
                   * enough apart that the circle round one does not touch the
                   * next. That spacing is the whole difference between a
                   * child showing what they know and a mess.
                   */
                  <span
                    key={option}
                    className="rounded-full border border-dashed border-stone px-4 py-1 text-[17px]"
                  >
                    {option}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      );

    case 'sort':
      return (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${part.columns.length}, minmax(0, 1fr))` }}
        >
          {part.columns.map((column) => (
            <div key={column}>
              <p className="mb-1 rounded-t border-2 border-b-0 border-espresso bg-linen px-2 py-0.5 text-center text-sm">
                {column}
              </p>
              {/* Room to glue something in or draw, rather than a cell to write in. */}
              <div className="border-2 border-espresso" style={{ height: part.height }} />
            </div>
          ))}
        </div>
      );

    case 'cutandpaste':
      return (
        <div>
          {part.glueLabel && <p className="mb-1 text-sm">{part.glueLabel}</p>}

          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: part.glueBoxes }, (_, i) => (
              <div
                key={i}
                className="flex items-end justify-center border-2 border-espresso pb-0.5 text-[10px] text-stone"
                style={{ height: GLUE_BOX }}
              >
                glue here
              </div>
            ))}
          </div>

          {/*
            The cut line. A row of scissors along a dashed rule is the
            convention every first grader already knows, and it tells them
            where to cut without anybody reading them an instruction.
          */}
          <div className="relative mt-4 flex items-center">
            <span className="text-sm leading-none">✂</span>
            <span className="ml-1 flex-1 border-t-2 border-dashed border-espresso" />
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {part.items.map((item) => (
              <span
                key={item}
                className="flex items-center justify-center border border-espresso px-3 text-[15px]"
                style={{ height: CUTOUT_STRIP - 24, minWidth: 96 }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      );

    /*
     * Never drawn. A break is where one sheet of paper ends — the splitting
     * happens before anything reaches here, so by this point there is nothing
     * left to render.
     */
    case 'pagebreak':
      return null;
  }
}

/**
 * Two columns, and a line drawn between the pairs.
 *
 * The dots matter: without something to aim at, a six-year-old's line lands
 * anywhere near the word and nobody can tell which one they meant. The right
 * column is deliberately in a different order from the left — the seed data
 * shuffles it — or there is nothing to work out.
 */
function Matching({ left, right }: { left: string[]; right: string[] }) {
  const rows = Math.max(left.length, right.length);

  return (
    <div style={{ display: 'grid', rowGap: 0 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3" style={{ height: MATCH_ROW }}>
          <span className="flex-1 text-right text-[17px]">{left[i] ?? ''}</span>
          <span className="h-2.5 w-2.5 shrink-0 rounded-full border-2 border-espresso" />
          <span className="w-16 shrink-0" />
          <span className="h-2.5 w-2.5 shrink-0 rounded-full border-2 border-espresso" />
          <span className="flex-1 text-[17px]">{right[i] ?? ''}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Three-line handwriting rules.
 *
 * Solid top and bottom with a dashed line through the middle — the same guide
 * they have on the wall. `trace` prints faintly on the first line so they have
 * something to copy rather than something to imagine.
 */
function Handwriting({ lines, trace }: { lines: number; trace?: string[] }) {
  return (
    <div style={{ display: 'grid', rowGap: RULE_GAP }}>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="relative" style={{ height: RULE_HEIGHT }}>
          <span className="absolute inset-x-0 top-0 border-t border-espresso" />
          <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-stone" />
          <span className="absolute inset-x-0 bottom-0 border-t border-espresso" />

          {trace?.[i] && (
            /*
             * Sized to the rules rather than to a number picked by eye: the
             * letters sit on the bottom line and reach the top one, which is
             * the whole point of tracing them. Letter-spacing spreads them
             * across the line instead of bunching them at the left.
             */
            <span
              className="absolute inset-x-1 bottom-0 flex items-end justify-between whitespace-nowrap leading-none text-stone/40"
              style={{ fontSize: RULE_HEIGHT - 6, top: 0 }}
              aria-hidden="true"
            >
              {trace[i]!.split('').map((ch, n) => (
                <span key={n}>{ch}</span>
              ))}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function NumberLine({ from, to, step }: { from: number; to: number; step: number }) {
  const ticks: number[] = [];
  for (let n = from; n <= to; n += step) ticks.push(n);

  return (
    <div className="pt-2">
      <div className="relative h-10 border-t-2 border-espresso">
        {ticks.map((n, i) => (
          <span
            key={n}
            className="absolute top-0 -translate-x-1/2"
            style={{ left: `${(i / (ticks.length - 1 || 1)) * 100}%` }}
          >
            <span className="block h-2 w-[2px] bg-espresso" />
            <span className="mt-0.5 block text-[11px]">{n}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
